import * as React from 'react';

import { enumDriverSortOption } from '@rematter/frontend-lib-api-client';
import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import { ComboBox, useComboBoxOptions } from '@rematter/ui-web';

import { useDriversQuery } from '~/modules/transportation/hooks/queries/useDriversQuery';
import { DriverRecord } from '~/modules/transportation/types';

interface DriverSelectProps {
  label: string;
  value: string;
  onChange: (value: React.Key | null) => void;
  isRequired?: boolean;
  isDisabled?: boolean;
  haulerId?: string | null;
}

export const DriverSelect = ({
  label,
  value,
  onChange,
  isRequired,
  isDisabled,
  haulerId,
}: DriverSelectProps) => {
  const t = useTranslations();
  const { data } = useDriversQuery({
    sorting: [
      enumDriverSortOption.FirstNameAsc,
      enumDriverSortOption.LastNameAsc,
    ],
    limit: 100,
    haulerId: haulerId || undefined,
  });

  const options = React.useMemo(() => data?.drivers ?? [], [data?.drivers]);

  const driverName = (driver: DriverRecord) =>
    `${driver.firstName} ${driver.lastName}`;

  const driverOptions = React.useMemo(
    () =>
      options.map((driver) => ({
        id: driver.id,
        label: driverName(driver),
      })),
    [options],
  );

  const { query, onChangeQuery, filteredOptions } = useComboBoxOptions({
    value: value || '',
    options: driverOptions,
    disabled: isDisabled,
  });

  return (
    <ComboBox
      label={label}
      value={value ? [value] : []}
      isRequired={isRequired}
      isDisabled={isDisabled}
      onChange={(values) => onChange(values[0] || null)}
      query={query}
      onChangeQuery={onChangeQuery}
      placeholder={t.servicesView.form.placeholders.driver}
      className="@DriverSelect"
      options={filteredOptions}
      getSelection={(ids) =>
        ids.map((id) => {
          const driver = options.find((d) => d.id === id);
          return { label: driver ? driverName(driver) : '' };
        })
      }
    />
  );
};
